// Whitelabel palette derivation.
//
// Customers linked to a partner — and partners themselves — get the
// `user.whitelabel` block computed in client/users/get-user. The block
// carries `brand_primary` and `brand_secondary` hex colors plus a `level`
// gate that controls how deep the branding goes (level >= 2 unlocks full
// whitelabel; level 1 stays minimal).
//
// This helper takes a base AppTheme and returns a palette-overlaid copy
// when whitelabel branding is active, or the base theme unchanged when it
// isn't. Mirrors the web theme's branding override logic in
// frontend/client/src/global-styles.js (themeBuilder) so both surfaces
// render the same colors against the same partner.
//
// Surfaces that pick up the override: primary (buttons, links, active
// nav state), and the dark-mode background swap when secondary is set.
// The web theme overrides 10+ tokens; we only mirror the ones that change
// the user-visible UI noticeably on mobile (primary + secondary). Adding
// more tokens is a one-line drop-in below if a designer wants it.

import type { AppTheme } from './index';

export interface Whitelabel {
  level?: number;
  brand_primary?: string | null;
  brand_secondary?: string | null;
}

const isHex = (v?: string | null): v is string =>
  !!v && /^#[0-9A-Fa-f]{6}$/.test(v);

export const getWhitelabelPalette = (
  baseTheme: AppTheme,
  whitelabel?: Whitelabel | null
): AppTheme => {
  if (!whitelabel) return baseTheme;
  const level = whitelabel.level ?? 1;
  // level === 1 (or unset) is "lightly branded" — we still apply the
  // primary swap because that's what controls button color and was
  // already enabled at level 1 historically. Full whitelabel (level >= 2)
  // unlocks the secondary as well so headers / footers / sidebars take
  // the partner accent.
  const primary = isHex(whitelabel.brand_primary) ? whitelabel.brand_primary : null;
  const secondary = level >= 2 && isHex(whitelabel.brand_secondary) ? whitelabel.brand_secondary : null;
  if (!primary && !secondary) return baseTheme;

  return {
    ...baseTheme,
    colors: {
      ...baseTheme.colors,
      ...(primary ? { primary } : {}),
      // sidebarActive picks up a translucent variant of the primary so the
      // active-nav row tints toward the partner color without stealing
      // attention from the active item. Keep alpha low (0.16) so it
      // matches the existing sidebarActive intensity.
      ...(primary ? { sidebarActive: hexToRgba(primary, 0.16) } : {}),
      ...(secondary ? { coreBackground: secondary } : {}),
    },
  };
};

// Tiny inline hex → rgba so we don't pull in a color helper. Falls back
// to the original input on any parse error so a malformed brand_primary
// can never crash the theme provider.
const hexToRgba = (hex: string, alpha: number): string => {
  const m = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex);
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
};
